import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { useAuth } from '../context/AuthContext';
import { getNotes } from '../api/sharedNotes';
import SharedNoteDialog from '../components/shared-notes/SharedNoteDialog';

const SharedNotes = () => {
  const { user, isAuthenticated } = useAuth();
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [skip, setSkip] = useState(0);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const limit = 10;

  const loadNotes = async (resetList = false, search = null) => {
    try {
      const currentSkip = resetList ? 0 : skip;
      const searchQuery = search !== null ? search : searchTerm;
      const data = await getNotes(currentSkip, limit, null, searchQuery);
      
      if (resetList) {
        setNotes(data.items);
        setSkip(limit);
      } else {
        setNotes(prev => [...prev, ...data.items]);
        setSkip(prev => prev + limit);
      }
      
      setTotal(data.total);
      setHasMore(data.items.length === limit && (resetList ? limit : skip + limit) < data.total);
    } catch (err) {
      console.error('Error loading shared notes:', err);
      setError('Failed to load shared notes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadNotes(true);
  }, [searchTerm]);

  const handleNoteCreated = () => {
    setDialogOpen(false);
    loadNotes(true);
  };

  const loadMore = () => {
    if (!loading && hasMore) {
      loadNotes(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setSearchTerm(searchInput);
    setLoading(true);
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setSearchTerm('');
    setLoading(true);
  };

  if (loading && notes.length === 0) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="flex justify-center items-center min-h-[60vh]">
          <svg className="w-10 h-10 text-primary-600 dark:text-primary-400 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-ui-light-text-primary dark:text-ui-dark-text-primary mb-2">
            Shared Notes
          </h1>
          <p className="text-ui-light-text-secondary dark:text-ui-dark-text-secondary">
            Collaborative notes for your team
          </p>
        </div>
        {isAuthenticated && (
          <button
            onClick={() => setDialogOpen(true)}
            className="mt-4 sm:mt-0 px-4 py-2 bg-primary-600 text-white dark:text-ui-dark-text-primary rounded-md hover:bg-primary-700 dark:hover:bg-primary-600 transition-colors"
          >
            Create Note
          </button>
        )}
      </div>

      {/* Search Bar */}
      <div className="mb-6">
        <form onSubmit={handleSearch} className="flex gap-2">
          <div className="relative flex-1">
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search notes..."
              className="w-full px-4 py-2 pl-10 border border-ui-light-border dark:border-ui-dark-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-ui-dark-background dark:text-ui-dark-text-primary"
            />
            <svg className="absolute left-3 top-2.5 h-5 w-5 text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 bg-primary-600 text-white dark:text-ui-dark-text-primary rounded-md hover:bg-primary-700 dark:hover:bg-primary-600 disabled:opacity-50 transition-colors"
          >
            Search
          </button>
          {searchTerm && (
            <button
              type="button"
              onClick={handleClearSearch}
              className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-colors"
            >
              Clear
            </button>
          )}
        </form>
        {searchTerm && (
          <div className="mt-2 text-sm text-ui-light-text-secondary dark:text-ui-dark-text-secondary">
            {total > 0 ? `Found ${total} note${total !== 1 ? 's' : ''} matching "${searchTerm}"` : `No notes found matching "${searchTerm}"`}
          </div>
        )}
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-200 rounded-md border border-red-200 dark:border-red-800">
          {error}
        </div>
      )}

      {notes.length === 0 && !loading ? (
        <div className="text-center py-12">
          <svg className="w-16 h-16 mx-auto mb-4 text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <p className="text-ui-light-text-secondary dark:text-ui-dark-text-secondary text-lg">No notes yet</p>
          <p className="text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary mt-2">
            {isAuthenticated ? 'Create the first shared note!' : 'Sign in to create notes'}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {notes.map((note) => (
            <div key={note.id} className="bg-ui-light-card dark:bg-ui-dark-card rounded-lg shadow-md p-6 border border-ui-light-border dark:border-ui-dark-border">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <Link 
                      to={`/shared-notes/${note.id}`}
                      className="text-xl font-semibold text-ui-light-text-primary dark:text-ui-dark-text-primary hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                    >
                      {note.title}
                    </Link>
                    {note.is_pinned && (
                      <svg className="w-5 h-5 text-primary-600 dark:text-primary-400" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M4 13V7a4 4 0 018 0v6a4 4 0 01-8 0z" />
                        <path fillRule="evenodd" d="M6 3a2 2 0 00-2 2v1h10V5a2 2 0 00-2-2H6z" clipRule="evenodd" />
                      </svg>
                    )}
                  </div>
                  <div className="flex items-center gap-4 text-sm text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary mb-2">
                    <span>Created by {note.created_by.full_name}</span>
                    <span>•</span>
                    <span>{format(new Date(note.created_at), 'MMM d, yyyy')}</span>
                    {note.last_edited_by && note.last_edited_by.id !== note.created_by.id && (
                      <>
                        <span>•</span>
                        <span>Last edited by {note.last_edited_by.full_name}</span>
                      </>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 ml-4">
                  {note.can_edit && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200">
                      Can Edit
                    </span>
                  )}
                  {note.roles.length > 0 && (
                    <div className="flex gap-1">
                      {note.roles.map(role => (
                        <span 
                          key={role.id} 
                          className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-ui-light-background dark:bg-ui-dark-background text-ui-light-text-secondary dark:text-ui-dark-text-secondary border border-ui-light-border dark:border-ui-dark-border"
                        >
                          {role.name}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
          
          {hasMore && (
            <div className="text-center pt-6">
              <button
                onClick={loadMore}
                disabled={loading}
                className="px-6 py-2 bg-primary-600 text-white dark:text-ui-dark-text-primary rounded-md hover:bg-primary-700 dark:hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? 'Loading...' : 'Load More'}
              </button>
            </div>
          )}
        </div>
      )}

      <SharedNoteDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onNoteCreated={handleNoteCreated}
      />
    </div>
  );
};

export default SharedNotes;